// Stock counts --- admins' Report (Mitch, 2026-11-04: "only be available to admins … so we can just see how the counts are
// comparing to live stock numbers"; "more like our other views with sorting columns etc."): counts against the books,
// as a table like every other (DataTable: sorting, columns, views, Copy / CSV). By default each SKU's latest count in the
// period, which answers "what's off now"; "Every count" is the log. A note opens in full where it's clicked. A difference
// is fixed by clicking the number under Books now (StockNumber, like any stock edit): counts never change stock.
import { useMemo, useState } from "react";
import { COUNT_REASON_LABELS, type CountReason } from "../../../../shared/counts.ts";
import { planToday } from "../../../../shared/day.ts";
import type { Item, StockCount } from "../../../../shared/db.gen.ts";
import { dayText, money, qty } from "../../lib/format.ts";
import { setSearch, useSearch } from "../../lib/router.tsx";
import { useRows } from "../../lib/sync.ts";
import { Badge, cx, Select } from "../../ui/controls.tsx";
import { type Column, DataTable } from "../../ui/DataTable.tsx";
import { PageSearch } from "../../ui/ListPage.tsx";
import { Figure, Figures } from "../../ui/PageParts.tsx";
import { Popover } from "../../ui/Popover.tsx";
import { openPane, PaneLink, usePaneRoot } from "../../ui/SidePane.tsx";
import { StockNumber } from "../../ui/StockEditor.tsx";

const PERIODS = [
  { value: "7", label: "Last 7 days" },
  { value: "30", label: "Last 30 days" },
  { value: "90", label: "Last 90 days" },
  { value: "365", label: "Last year" },
  { value: "all", label: "All time" },
];

type Status = "off" | "changed" | "matched";
const STATUS_LABEL: Record<Status, string> = { off: "Off", changed: "Stock changed since", matched: "Matched" };
const STATUS_TONE: Record<Status, "danger" | "accent" | "ok"> = { off: "danger", changed: "accent", matched: "ok" };
const STATUS_RANK: Record<Status, number> = { off: 0, changed: 1, matched: 2 };

type Row = {
  id: string;
  count: StockCount;
  item: Item | undefined;
  diff: number;
  pct: number | null;
  value: number | null;
  now: number;
  status: Status;
  changedAt: string | null;
  times: number;
  by: string;
  reason: string;
};

const itemPage = (i: Item) => (i.kind === "sell" ? `/items/sell?open=${i.id}` : `/parts?open=${i.id}`);
const when = (at: string) => `${dayText(planToday(new Date(at)))} ${new Date(at).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })}`;
const signed = (n: number) => (n > 0 ? `+${qty(n)}` : qty(n));

export function CountsReport() {
  const params = useSearch();
  const days = params.get("days") ?? "30";
  const every = params.get("every") === "1";
  const diffsOnly = params.get("diffs") === "1";
  const [query, setQuery] = useState("");
  const openItem = usePaneRoot("item");

  const counts = useRows("stock_count");
  const items = useRows("item");
  const users = useRows("app_user");
  const levels = useRows("stock_level");
  const balances = useRows("stock_balance");
  const changes = useRows("stock_change");
  const tasks = useRows("count_task");

  const rows = useMemo(() => {
    const itemById = new Map(items.map((i) => [i.id, i]));
    const userById = new Map(users.map((u) => [u.id, u.name]));
    const taskById = new Map(tasks.map((t) => [t.id, t]));
    const onHand = new Map<string, number>();
    for (const l of levels) onHand.set(`${l.item_id}|${l.location_id}`, Number(l.on_hand));
    for (const b of balances) if (!onHand.has(`${b.item_id}|${b.location_id}`)) onHand.set(`${b.item_id}|${b.location_id}`, Number(b.qty));
    // The last stock change made to each item at each location (Shopify's counts changed from HHL Ops).
    const lastChange = new Map<string, string>();
    for (const c of changes) {
      if (c.status === "failed") continue;
      const k = `${c.item_id}|${c.location_id}`;
      if (!lastChange.has(k) || (lastChange.get(k) as string) < c.created_at) lastChange.set(k, c.created_at);
    }
    const since = days === "all" ? "" : new Date(Date.now() - Number(days) * 86_400_000).toISOString();
    const inPeriod = counts.filter((c) => c.counted_at >= since).sort((a, b) => b.counted_at.localeCompare(a.counted_at));
    const times = new Map<string, number>();
    for (const c of inPeriod) times.set(`${c.item_id}|${c.location_id}`, (times.get(`${c.item_id}|${c.location_id}`) ?? 0) + 1);
    const seen = new Set<string>();
    const out: Row[] = [];
    for (const c of inPeriod) {
      const k = `${c.item_id}|${c.location_id}`;
      if (!every && seen.has(k)) continue; // the newest count of each SKU
      seen.add(k);
      const item = itemById.get(c.item_id);
      const diff = Number(c.counted) - Number(c.expected);
      const cost = item?.std_cost == null ? null : Number(item.std_cost);
      const changedAt = lastChange.get(k) && (lastChange.get(k) as string) > c.counted_at ? (lastChange.get(k) as string) : null;
      const status: Status = diff === 0 ? "matched" : changedAt ? "changed" : "off";
      const task = c.task_id ? taskById.get(c.task_id) : undefined;
      out.push({
        id: c.id,
        count: c,
        item,
        diff,
        pct: Number(c.expected) > 0 ? (diff / Number(c.expected)) * 100 : null,
        value: cost == null ? null : Math.round(diff * cost * 100) / 100,
        now: onHand.get(k) ?? 0,
        status,
        changedAt,
        times: times.get(k) ?? 1,
        by: (c.counted_by && userById.get(c.counted_by)) || "",
        reason: task ? COUNT_REASON_LABELS[task.reason as CountReason] : "Extra",
      });
    }
    return out;
  }, [counts, items, users, levels, balances, changes, tasks, days, every]);

  const shown = diffsOnly ? rows.filter((r) => r.diff !== 0) : rows;
  // The period's numbers, by SKU (each one's latest count) whatever the table shows.
  const latest = useMemo(() => {
    const seen = new Set<string>();
    return rows.filter((r) => {
      const k = `${r.count.item_id}|${r.count.location_id}`;
      if (seen.has(k)) return false;
      seen.add(k);
      return true;
    });
  }, [rows]);
  const matched = latest.filter((r) => r.status === "matched").length;
  const offNow = latest.filter((r) => r.status === "off");
  const net = latest.reduce((n, r) => n + r.diff, 0);
  const netValue = latest.reduce((n, r) => n + (r.value ?? 0), 0);
  const unpriced = latest.filter((r) => r.diff !== 0 && r.value == null).length;

  const columns = useMemo<Column<Row>[]>(
    () => [
      {
        key: "sku",
        header: "SKU",
        width: "140px",
        value: (r) => r.item?.sku ?? "",
        cell: (r) =>
          r.item ? (
            <PaneLink kind="item" id={r.item.id} to={itemPage(r.item)} className="truncate font-mono text-[12px] hover:underline">
              {r.item.sku}
            </PaneLink>
          ) : null,
      },
      {
        key: "name",
        header: "Name",
        width: "minmax(180px,1.4fr)",
        value: (r) => r.item?.name ?? "",
        cell: (r) => <span className="truncate">{r.item?.name}</span>,
      },
      {
        key: "counted_at",
        header: "Counted",
        width: "140px",
        value: (r) => r.count.counted_at,
        text: (r) => when(r.count.counted_at),
        cell: (r) => <span className="text-muted">{when(r.count.counted_at)}</span>,
      },
      {
        key: "counted",
        header: "Counted",
        headerTitle: "What was on the shelf",
        width: "84px",
        align: "right",
        value: (r) => Number(r.count.counted),
        cell: (r) => <span className="font-mono">{qty(Number(r.count.counted))}</span>,
      },
      {
        key: "expected",
        header: "Books then",
        headerTitle: "On hand by the books when the count was entered",
        width: "92px",
        align: "right",
        value: (r) => Number(r.count.expected),
        cell: (r) => <span className="font-mono text-muted">{qty(Number(r.count.expected))}</span>,
      },
      {
        key: "diff",
        header: "Difference",
        headerTitle: "Counted − books then: below zero, fewer on the shelf than the books say",
        width: "96px",
        align: "right",
        value: (r) => r.diff,
        cell: (r) => (
          <span className={cx("font-mono", r.diff < 0 ? "text-danger" : r.diff > 0 ? "text-warn" : "text-faint")}>{r.diff ? signed(r.diff) : "—"}</span>
        ),
      },
      {
        key: "pct",
        header: "Diff %",
        headerTitle: "The difference as a share of the books then",
        width: "76px",
        align: "right",
        value: (r) => r.pct,
        text: (r) => (r.pct == null ? "" : Math.round(r.pct * 10) / 10),
        cell: (r) => <span className="font-mono text-muted">{r.pct == null || !r.diff ? "" : `${r.pct > 0 ? "+" : ""}${Math.round(r.pct)}%`}</span>,
      },
      {
        key: "value",
        header: "Value",
        headerTitle: "The difference at the item's cost (Shopify's cost per item)",
        width: "96px",
        align: "right",
        value: (r) => r.value,
        cell: (r) => (
          <span className={cx("font-mono", (r.value ?? 0) < 0 ? "text-danger" : "text-muted")}>
            {r.value == null ? (r.diff ? "no cost" : "") : r.diff ? money(r.value, "USD") : ""}
          </span>
        ),
      },
      {
        key: "now",
        header: "Books now",
        headerTitle: "On hand by the books now: click to change the stock",
        width: "92px",
        align: "right",
        value: (r) => r.now,
        cell: (r) => (
          <StockNumber itemId={r.count.item_id} title="On hand now" className="font-mono">
            {qty(r.now)}
          </StockNumber>
        ),
      },
      {
        key: "status",
        header: "Since",
        headerTitle: "Matched; Off (no stock change since the count); or the stock was changed since",
        width: "150px",
        value: (r) => STATUS_RANK[r.status],
        text: (r) => (r.changedAt ? `${STATUS_LABEL[r.status]} ${dayText(planToday(new Date(r.changedAt)))}` : STATUS_LABEL[r.status]),
        cell: (r) => (
          <span title={r.changedAt ? `Changed ${when(r.changedAt)}` : undefined}>
            <Badge tone={STATUS_TONE[r.status]}>{STATUS_LABEL[r.status]}</Badge>
          </span>
        ),
      },
      { key: "by", header: "Counted by", width: "120px", value: (r) => r.by, cell: (r) => <span className="truncate text-muted">{r.by}</span> },
      {
        key: "note",
        header: "Note",
        width: "minmax(160px,1.6fr)",
        value: (r) => r.count.note,
        cell: (r) => (r.count.note ? <NoteCell note={r.count.note} /> : null),
      },
      {
        key: "times",
        header: "Times counted",
        headerTitle: "Counts of it in the period",
        width: "84px",
        align: "right",
        optional: true,
        value: (r) => r.times,
      },
      { key: "reason", header: "On the list as", width: "120px", optional: true, value: (r) => r.reason },
    ],
    [],
  );

  return (
    <div className="flex h-full min-h-0 flex-col">
      {/* The period's numbers, then the table with its own search and filters (Mitch, 2026-11-04: a table and its
          controls are one unit) */}
      <Figures className="shrink-0 border-b border-line px-4 py-3">
        <Figure label="SKUs counted" value={qty(latest.length)} sub={`${qty(rows.length === latest.length ? latest.length : rows.length)} counts`} />
        <Figure
          label="Matched the books"
          value={latest.length ? `${Math.round((matched / latest.length) * 100)}%` : "—"}
          sub={`${qty(matched)} of ${qty(latest.length)} SKUs`}
        />
        <Figure
          label="Counted − books"
          value={`${signed(net)} units`}
          sub={`${money(Math.round(netValue * 100) / 100, "USD")} at cost${unpriced ? ` (${unpriced} without a cost)` : ""}`}
          tone={net < 0 ? "danger" : undefined}
        />
        <Figure label="Still off" value={qty(offNow.length)} sub="SKUs whose stock hasn't changed since" tone={offNow.length ? "warn" : undefined} />
      </Figures>
      <div className="min-h-0 flex-1">
        <DataTable
          rows={shown}
          columns={columns}
          search={query}
          activeId={shown.find((r) => r.count.item_id === openItem)?.id ?? null}
          defaultSort={{ key: "status" }}
          onOpen={(r) => openPane({ kind: "item", id: r.count.item_id }, { root: true, replace: !!openItem })}
          rowNoun={["count", "counts"]}
          exportName="Stock counts"
          bar={{
            search: (
              <PageSearch value={query} onChange={setQuery} placeholder="Filter SKUs, notes, people…" toTable className="max-w-64 min-w-32 flex-1 basis-40" />
            ),
            filters: (
              <>
                <div className="w-36 shrink-0">
                  <Select
                    aria-label="Period"
                    value={days}
                    onChange={(e) => setSearch({ days: e.target.value === "30" ? null : e.target.value })}
                    options={PERIODS}
                    className="h-7 text-[12px]"
                  />
                </div>
                <div className="flex shrink-0 overflow-hidden rounded-md border border-line text-[12px]">
                  {[
                    { on: !every, label: "Latest per SKU", set: null },
                    { on: every, label: "Every count", set: "1" },
                  ].map((b) => (
                    <button
                      key={b.label}
                      type="button"
                      onClick={() => setSearch({ every: b.set })}
                      className={cx("px-2.5 py-1", b.on ? "bg-surface-2 font-medium" : "text-muted hover:bg-hover hover:text-text")}
                    >
                      {b.label}
                    </button>
                  ))}
                </div>
                <label className="flex shrink-0 items-center gap-1.5 text-[12px] text-muted">
                  <input type="checkbox" checked={diffsOnly} onChange={(e) => setSearch({ diffs: e.target.checked ? "1" : null })} />
                  Differences only
                </label>
              </>
            ),
          }}
          empty={
            <div className="p-6 text-center text-muted">
              {counts.length ? "No counts match." : "No counts yet: they come in as the daily lists are counted."}
            </div>
          }
        />
      </div>
    </div>
  );
}

/** A note, cut to its cell: a click shows all of it, where it was clicked. */
function NoteCell({ note }: { note: string }) {
  return (
    <Popover
      width={360}
      button={(toggle) => (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            toggle();
          }}
          className="min-w-0 truncate text-left text-[12px] text-muted hover:text-text"
        >
          {note}
        </button>
      )}
    >
      {() => <p className="text-[13px] whitespace-pre-wrap">{note}</p>}
    </Popover>
  );
}
